import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Receipt, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email.trim(), password);
    setSubmitting(false);
    if (ok) {
      toast.success("Welcome back!");
      nav("/");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div
        className="hidden md:block relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1714636608872-048fc9231892?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxiZWlnZSUyMGFic3RyYWN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3ODc1NzcyMjJ8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/45 to-black/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white/95 text-primary flex items-center justify-center">
              <Receipt weight="duotone" size={22} />
            </div>
            <div className="font-display text-lg font-semibold">EvoPlus Invoice</div>
          </div>
          <div>
            <div className="font-display text-3xl leading-tight tracking-tight">
              Import an Excel. Approve. Generate finished PDFs.
            </div>
            <p className="mt-3 text-white/85 text-sm max-w-md">
              Split each issuer's total into exact invoices capped at ₹3,00,000 — unequal
              amounts, precise Indian currency, and clean monochrome A4-ready PDFs with
              audited approval controls.
            </p>
          </div>
          <div className="text-xs text-white/70">Capped at ₹3,00,000. Auditable. A4-ready.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <form className="w-full max-w-sm space-y-5" onSubmit={onSubmit} data-testid="login-form">
          <div>
            <div className="font-display text-2xl font-semibold">Sign in</div>
            <div className="text-sm text-muted-foreground mt-1">
              Enter your email and password to continue.
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
          </div>
          {error && (
            <div className="text-sm text-destructive" data-testid="login-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={submitting} className="w-full" data-testid="login-submit">
            {submitting ? "Signing in..." : "Sign in"} <ArrowRight size={16} className="ml-1" />
          </Button>
        </form>
      </div>
    </div>
  );
}
